import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { CallState, Experiment, ExperimentState } from "redmond";
import { ExperimentsContext } from "@capone/experiments";
import { fetchActiveExperiments } from "../api/v0/experiments/fetchExperiments";

// Active Experiments

export const HOTELS_SHOP_IMAGE_MODAL = "c1-marketplace-hotel-shop-image-modal";
export const TREES_MODAL_EXPERIMENT = "c1-marketplace-trees";
export const TRAVEL_WALLET_OFFER_EXPERIMENT =
  "c1-marketplace-travel-wallet-offers";
export const CASH_VALUEPROP_EXPERIMENT = "c1-marketplace-cash-valueprop-merch";
export const SIMILAR_HOTELS_EXPERIMENT = "c1-marketplace-similar-hotels";
export const TRAVEL_WALLET_CREDITS_EXPERIMENT =
  "c1-marketplace-travel-wallet-credits";
export const PRICE_MATCH_CREDITS_EXPERIMENT =
  "c1-marketplace-price-match-credits";
export const CHECKOUT_REWARDS_EARN_BANNER_EXPERIMENT =
  "corp-checkout-rewards-earn-banner";
export const PREFERRED_HOTEL_EXPERIMENT =
  "c1-marketplace-cot-preferred-hotel-merch";
export const HOTEL_INFO_CHECKOUT_UPDATE_EXPERIMENT =
  "c1-marketplace-hotel-info-checkout-update";
export const ALL_CARDS_IN_PHC = "b2b-agency-all-cards-in-phc";
export const VCN_ENABLEMENT = "c1-marketplace-vcn-enablement";
// TODO - CFAR Premier: once CFAR is rolled out to 100%, we could consider replacing this flag with c1-fintech-hotel-cfar
export const HOTELS_CFAR = "c1-fintech-premier-collection-cfar";
export const CREDIT_OFFER_STACKING_V1 =
  "c1-marketplace-credit-offer-stacking-v1";
export const TRAVEL_CREDIT_HISTORY_EXPERIMENT =
  "c1-marketplace-credit-transaction-history";
export const ANNUAL_TRAVEL_CREDITS = "c1-marketplace-annual-travel-credits";
export const C1_AUTO_SELECT_CARD_AT_CHECKOUT =
  "c1-auto-select-card-at-checkout";
export const LC_GATING_EXPERIMENT =
  "c1-marketplace-lifestyle-hotels-collection";
export const PREMIER_COLLECTION_EXPERIMENT =
  "c1-marketplace-luxury-hotels-collection";
export const TRAVEL_SALE = "c1-marketplace-travel-sale";
export const VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT =
  "c1-marketplace-vr-premium-cards";
export const VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT =
  "c1-marketplace-vr-non-premium-cards";
export const VR_CALENDAR_EXPERIMENT = "c1-vacation-rentals-calendar";
export const HOTEL_CFAR_MODEL_V1 = "c1-fintech-hotel-cfar-model-v1";
export const HOTELS_TAXES_AND_FEES_EXPERIMENT =
  "c1-marketplace-HotelsCaliforniaBill537";
export const GLOBAL_MOBILE_NAV_EXPERIMENT = "c1-marketplace-global-mobile-nav";
export const FINTECH_HOTEL_UX_UPDATED = "c1-fintech-hotel-UX-updated";
export const HOTELS_CALIFORNIA_BILL_644_EXPERIMENT =
  "c1-marketplace-HotelsCaliforniaBill644";
export const LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH =
  "c1-luxury-vacation-rental-funnel-launch";
export const CACHE_HOTEL_TOKEN = "c1-marketplace-cache-hotel-token";
export const LUXURY_DATELESS_SEARCH_EXPERIMENT = "c1-luxury-dateless-search";
export const LUXURY_DATELESS_SEARCH_UNAVAILABLE_EXPERIMENT =
  "c1-luxury-dateless-search-unavailable";
export const LUXURY_VACATION_RENTALS_REFRESH = "c1-luxury-vr-refresh";
// When on, the luxury VR shop runs in the replatformed app, which doesn't fill
// this module's store — so the book page loads its data from the URL instead.
export const CORE_LUXURY_VACATION_RENTALS_SHOP =
  "c1-replatform-luxury-vacation-rentals-shop";
export const SHOW_MEALPLAN = "c1-show-mealplan";
export const FTC_PRICING_UPDATE = "c1-marketplace-ftc-pricing-update";
export const XMOD_V1_EXPERIMENT = "c1-marketplace-xmod-v1";
export const VR_PC_PRIORITY_SORT_EXPERIMENT =
  "c1-marketplace-vr-pc-priority-sort";
export const LUXURY_PAY_AT_HOTEL_BADGE = "c1-luxury-pay-at-hotel-badge";
export const HOTELS_CANCELLATION_POLICY_EXPERIMENT =
  "c1-hotels-supply-cancellation-policy";
export const TRAVEL_APP_GTM_EXPERIMENT = "c1-portex-app-gtm";

export const CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS =
  "corp-ftb-offer-additional-placements";

export const VESPER_PC_CREDIT = "c1-vesper-pc-credit";

// Default variants
export const CONTROL = "control";
export const AVAILABLE = "available";
export const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
export type DefaultVariantType = typeof CONTROL | typeof AVAILABLE;
export const VARIANT_1 = "variant-1";
export const VARIANT_2 = "variant-2";

// Variants for HotelPromotion
export const LODGING_PROMOTIONS = "HotelPromotion";
export const LODGING_PROMOTIONS_AVAILABLE = "Available";
export const LODGING_PROMOTIONS_VARIANTS = [
  CONTROL,
  LODGING_PROMOTIONS_AVAILABLE,
] as const;
export type LodgingPromotionsPropVariantType =
  | typeof CONTROL
  | typeof LODGING_PROMOTIONS_AVAILABLE;

// JSON Response type
// right now typing the response of this experiment: https://app.launchdarkly.com/capital-one/test/features/c1-fintech-hotel-cfar-model-v1/variations
// as this looks like the only JSON experiment that we are consuming.
// if this needs to be generalized then we will do so at a later time.
export interface LDJSONConfig {
  data: {
    model: string;
    version: string;
  };
  variantName: string;
}

// Variants for c1-marketplace-cash-valueprop-merch
export const CASH_VALUEPROP_A = "cash-valueprop-a";
export const CASH_VALUEPROP_B = "cash-valueprop-b";
export const CASH_VALUEPROP_C = "cash-valueprop-c";
export const CASH_VALUEPROP_VARIANTS = [
  CONTROL,
  CASH_VALUEPROP_A,
  CASH_VALUEPROP_B,
  CASH_VALUEPROP_C,
] as const;
export type CashValuePropVariantType =
  | typeof CONTROL
  | typeof CASH_VALUEPROP_A
  | typeof CASH_VALUEPROP_B
  | typeof CASH_VALUEPROP_C;

// Variants for c1-marketplace-similar-hotels
export const SIMILAR_HOTELS_1A = "1A";
export const SIMILAR_HOTELS_1B = "1B";
export const SIMILAR_HOTELS_VARIANTS = [
  CONTROL,
  SIMILAR_HOTELS_1A,
  SIMILAR_HOTELS_1B,
] as const;
export type SimilarHotelsPropVariantType =
  | typeof CONTROL
  | typeof SIMILAR_HOTELS_1A
  | typeof SIMILAR_HOTELS_1B;

// Variants for c1-marketplace-cot-preferred-hotel-merch
export const PREFERRED_HOTELS_V1 = "v1-special-rates";
export const PREFERRED_HOTELS_VARIANT = [CONTROL, PREFERRED_HOTELS_V1] as const;
export type PreferredHotelsPropVariantType =
  | typeof CONTROL
  | typeof PREFERRED_HOTELS_V1;

// Variants for c1-marketplace-travel-sale
export const TRAVEL_SALE_LEAD_UP = "lead-up";
export const TRAVEL_SALE_ACTIVE = "active";
export const TRAVEL_SALE_VARIANTS = [
  CONTROL,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_ACTIVE,
] as const;
export type TravelSalePropVariantType =
  | typeof CONTROL
  | typeof TRAVEL_SALE_LEAD_UP
  | typeof TRAVEL_SALE_ACTIVE;

// variants for c1-fintech-hotel-cfar-model-v1
export const CFAR_MODEL_ADVANCE_DAYS_10_21 = "advanceDays10and21";
export const CFAR_MODEL_RANDOM_PERCENT_16_26 = "randomPercent16to26";
export const CFAR_MODEL_RANDOM_PERCENT_5_30_FR_ELIGIBLE =
  "randomPercent5to30FrEligible";
export const CFAR_MODEL_ADVANCE_STAR_RATING_23_28 =
  "advanceAndStarRating23and28";
export const CFAR_MODEL_ADVANCE_STAR_RATING_23_28_COMPARISON =
  "advanceAndStarRating23and28Comparison";
export const CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE =
  "advAndStarRating23and28FrEligible";
export const CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY =
  "advanceAndStarRating23and28FrEligibleCopy";
export const CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_NEW_UX =
  "advanceAndStarRating23and28FrEligibleNewUx";
export const CFAR_MODEL_V1_VARIANTS = [
  CFAR_MODEL_ADVANCE_DAYS_10_21,
  CFAR_MODEL_RANDOM_PERCENT_16_26,
  CFAR_MODEL_RANDOM_PERCENT_5_30_FR_ELIGIBLE,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_COMPARISON,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY,
  CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_NEW_UX,
] as const;
export type CfarModelV1VariantType =
  | typeof CFAR_MODEL_ADVANCE_DAYS_10_21
  | typeof CFAR_MODEL_RANDOM_PERCENT_16_26
  | typeof CFAR_MODEL_RANDOM_PERCENT_5_30_FR_ELIGIBLE
  | typeof CFAR_MODEL_ADVANCE_STAR_RATING_23_28
  | typeof CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE
  | typeof CFAR_MODEL_ADVANCE_STAR_RATING_23_28_COMPARISON
  | typeof CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_COPY
  | typeof CFAR_MODEL_ADVANCE_STAR_RATING_23_28_FR_ELIGIBLE_NEW_UX;

// Variants for c1-marketplace-HotelsCaliforniaBill537
export const LEAST_DISRUPTION = "least-disruption";
export const BEST_COMPLIANCE = "best-compliance";
export const HOTELS_TAXES_AND_FEES_VARIANTS = [
  CONTROL,
  LEAST_DISRUPTION,
  BEST_COMPLIANCE,
] as const;
export type HotelsTaxesAndFeesVariantType =
  | typeof CONTROL
  | typeof LEAST_DISRUPTION
  | typeof BEST_COMPLIANCE;

export const ALLOWED_PAY_LATER_LODGINGS =
  "c1-marketplace-allowed-pay-later-lodgings";
export interface AllowedPayLaterLodgingsConfig extends Omit<LDJSONConfig, 'data'> {
  data: { lodgingIds: string[] };
}

export const LUX_Q2_VR_OFFER_CTAS = "c1-luxury-q2-vr-offer-ctas";
export interface LuxQ2VROfferCtasConfig extends Omit<LDJSONConfig, 'data'> {
  offerIds: string[];
}

interface ExperimentStateWithCallState extends ExperimentState {
  callState: CallState;
}

const defaultInitializer = (): ExperimentStateWithCallState => {
  return {
    experiments: [],
    trackingProperties: undefined,
    callState: CallState.NotCalled,
  };
};

// readonly (together with const restriction below) ensures typeof supportedVariants[number]
// can be recognized by transpiler as an array of constants
// [string, ...string[]] ensures array is not empty.
export function getExperimentVariantCustomVariants<
  T extends readonly [string, ...string[]],
>(
  experiments: Array<Experiment>,
  experimentId: string,
  // List of supported variants. If the variant retreived is not recognized by the code, the code will fall back to the first variant.
  // Note the input list needs to be declared as const e.g. const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
  supportedVariants: T,
): (typeof supportedVariants)[number] {
  const experiment = experiments?.find((exp) => exp.id === experimentId);

  if (experiment) {
    if (supportedVariants.includes(experiment.variant)) {
      return experiment.variant;
    } else {
      return supportedVariants[0];
    }
  } else {
    return supportedVariants[0];
  }
}

export function getExperimentVariant(
  experiments: Array<Experiment>,
  experimentId: string,
): DefaultVariantType {
  return getExperimentVariantCustomVariants(
    experiments,
    experimentId,
    DEFAULT_VARIANTS,
  );
}

export function getExperimentVariantJSON(
  experiments: Array<Experiment>,
  experimentId: string,
): LDJSONConfig | null {
  const experiment = experiments.find((exp) => exp.id === experimentId);
  if (!experiment) {
    return null;
  }
  return JSON.parse(experiment.variant);
}

// Re-export the shared useExperimentsContext as useExperiments for backwards compatibility
export { useExperimentsContext as useExperiments } from "@capone/experiments";

export function addTrackingProperties(
  trackingProperties: Map<string, string> | undefined,
  properties?: any,
): unknown {
  if (!properties) {
    properties = {};
  }
  if (trackingProperties) {
    properties["experiments"] = Object.keys(trackingProperties).map(
      (ex) => `${ex}_${trackingProperties![ex]}`,
    );
  }
  return properties;
}

const ExperimentsProvider: FC<
  PropsWithChildren<{ initState?: ExperimentStateWithCallState }>
> = ({ initState = defaultInitializer(), children }) => {
  // TODO: use tracking reducers
  const [state, setState] = useState(initState);

  useEffect(() => {
    if (!initState?.experiments.length) {
      const fetchExperiments = async () => {
        setState({ ...state, callState: CallState.InProcess });
        await fetchActiveExperiments().then((result) => {
          setState({
            ...(result as ExperimentState),
            callState: CallState.Success,
          });
        });
      };
      fetchExperiments();
    }
  }, []);

  return (
    <ExperimentsContext.Provider value={state}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export default ExperimentsProvider;
